import { useCallback, useEffect, useRef, useState } from "react";

export type RecorderStatus = "idle" | "recording" | "paused" | "review" | "denied";

// Pick a container the browser can record AND our upload allowlist accepts.
// Chrome/Firefox → webm; Safari → mp4. Both exts are in ALLOWED_EXTS (api.ts),
// so a recorded blob rides the exact same presign → PUT → register path as a file.
export function pickAudioFormat(): { mimeType: string; ext: string } | null {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    { mimeType: "audio/webm", ext: "webm" },
    { mimeType: "audio/mp4", ext: "mp4" },
  ];
  return candidates.find((c) => MediaRecorder.isTypeSupported(c.mimeType)) ?? null;
}

// e.g. recording-2026-07-01-1423.webm
export function recordingFilename(date: Date, ext: string): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}` +
    `-${p(date.getHours())}${p(date.getMinutes())}`;
  return `recording-${stamp}.${ext}`;
}

export interface Recorder {
  status: RecorderStatus;
  elapsed: number; // seconds of captured audio (paused time excluded)
  error: string | null;
  start: () => Promise<void>;
  togglePause: () => void;
  stop: () => void; // manual finish → onComplete(file)
  uploadReview: () => void; // keep the capped take → onComplete(file)
  discard: () => void; // drop the take / clear an error
}

// Browser mic recorder around MediaRecorder. A manual Stop hands the finished
// file straight to onComplete (which uploads it). Hitting maxSeconds auto-stops
// into a "review" state so the user can keep or discard the capped take — free
// users are capped so a recording can never exceed their per-recording minutes
// and waste a transcription (the server re-enforces the same cap regardless).
export function useRecorder(maxSeconds: number, onComplete: (file: File) => void): Recorder {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fmtRef = useRef<{ mimeType: string; ext: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dispositionRef = useRef<"complete" | "review">("complete");
  const reviewFileRef = useRef<File | null>(null);
  // Latest onComplete without re-subscribing the MediaRecorder handlers.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  function clearTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }
  function releaseMic() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }
  function tick() {
    clearTimer();
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  }

  // Finalize once MediaRecorder flushes its last chunk (fires after .stop()).
  const handleStop = useCallback(() => {
    clearTimer();
    releaseMic();
    const fmt = fmtRef.current;
    if (!fmt || chunksRef.current.length === 0) {
      setStatus("idle");
      setElapsed(0);
      return;
    }
    const blob = new Blob(chunksRef.current, { type: fmt.mimeType });
    const file = new File([blob], recordingFilename(new Date(), fmt.ext), { type: fmt.mimeType });
    chunksRef.current = [];
    if (dispositionRef.current === "review") {
      reviewFileRef.current = file;
      setStatus("review");
    } else {
      setStatus("idle");
      setElapsed(0);
      onCompleteRef.current(file);
    }
  }, []);

  const start = useCallback(async () => {
    const fmt = pickAudioFormat();
    if (!fmt) {
      setError("Recording isn't supported in this browser.");
      setStatus("denied");
      return;
    }
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      fmtRef.current = fmt;
      chunksRef.current = [];
      reviewFileRef.current = null;
      dispositionRef.current = "complete";
      const rec = new MediaRecorder(stream, { mimeType: fmt.mimeType });
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = handleStop;
      recRef.current = rec;
      rec.start(1000); // flush a chunk each second so nothing is lost on stop
      setElapsed(0);
      setStatus("recording");
      tick();
    } catch (err) {
      releaseMic();
      setStatus("denied");
      setError(
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Microphone access denied. Allow it in your browser, then try again."
          : "Couldn't start recording. Is a microphone connected?",
      );
    }
  }, [handleStop]);

  const togglePause = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      clearTimer();
      setStatus("paused");
    } else if (rec.state === "paused") {
      rec.resume();
      setStatus("recording");
      tick();
    }
  }, []);

  const stop = useCallback(() => {
    dispositionRef.current = "complete";
    clearTimer();
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop(); // → handleStop
    } else {
      releaseMic();
      setStatus("idle");
      setElapsed(0);
    }
  }, []);

  const uploadReview = useCallback(() => {
    const file = reviewFileRef.current;
    reviewFileRef.current = null;
    setStatus("idle");
    setElapsed(0);
    if (file) onCompleteRef.current(file);
  }, []);

  const discard = useCallback(() => {
    reviewFileRef.current = null;
    chunksRef.current = [];
    setError(null);
    setStatus("idle");
    setElapsed(0);
  }, []);

  // Cap reached → auto-stop into review (handleStop sees disposition "review").
  useEffect(() => {
    if ((status === "recording" || status === "paused") && maxSeconds > 0 && elapsed >= maxSeconds) {
      dispositionRef.current = "review";
      clearTimer();
      const rec = recRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
    }
  }, [elapsed, status, maxSeconds]);

  // Release the mic if we unmount mid-recording.
  useEffect(
    () => () => {
      clearTimer();
      releaseMic();
    },
    [],
  );

  return { status, elapsed, error, start, togglePause, stop, uploadReview, discard };
}
